



























































































































































































































































import {
  PropType,
  Ref,
  computed,
  defineComponent,
  ref,
  toRef,
  watch,
} from '@nuxtjs/composition-api';
import {
SfAlert,
SfBadge,
SfGallery,
SfLoader,
} from '@storefront-ui/vue';

import {
getName as getProductName,
getSwatchData as getProductSwatchData,
getProductSku,
} from '~/modules/catalog/product/getters/productGetters';

import {
  ConfigurableProductOptionsValues,
  MatchingBikeInfo,
  useFrametool,
} from '~/composables';
import { getConfigurableProductPriceCommand } from '~/modules/catalog/pricing/getConfigurableProductPriceCommand';
import { getConfigurableProductSpecialPriceCommand } from '~/modules/catalog/pricing/getConfigurableProductSpecialPriceCommand';
import { useProductGallery } from '~/modules/catalog/product/composables/useProductGallery';
import { TabsConfig, useProductTabs } from '~/modules/catalog/product/composables/useProductTabs';
import { Product } from '~/modules/catalog/product/types';
import { useCart } from '~/modules/checkout/composables/useCart';
import { useUser } from '~/modules/customer/composables/useUser';
import useWishlist from '~/modules/wishlist/composables/useWishlist';

import { ConfigurableVariant } from '@vue-storefront/magento-api';
import { arrowRightIcon } from '~/cms/helpers/customIcons';
import { expertAdviceUrl, getExpertAdviceLink } from '~/cms/helpers/urls';
import BKButton from '~/components/BK/BKButton.vue';
import BKHeadlineText from '~/components/BK/BKHeadlineText.vue';
import BKPrice from '~/components/BK/BKPrice.vue';
import { bikeNameQueryParameter } from '~/helpers/routes';
import { ManufacturerInfo } from '~/graphql/getManufacturerInfo';
import { useManufacturerApi } from '../../../composables/useManufacturerApi';
import ProductAccordeon from '../../components/ProductAccordeon.vue';
import ProductFrames from '../../components/ProductFrames.vue';
import ProductTeaser from '../../components/ProductTeaser.vue';
import FinancingBlock from './FinancingBlock.vue';

export default defineComponent({
  name: 'ConfigurableBike',
  components: {
    SfGallery,
    SfLoader,
    SfAlert,
    SfBadge,
    BKButton,
    ProductAccordeon,
    BKHeadlineText,
    BKPrice,
    ProductTeaser,
    ProductFrames,
    AddBikeToCartModal: () => import(/* webpackPrefetch: true */ '../../components/modals/AddBikeToCartModal.vue'),
    FavouriteBikePositionModal: () => import(/* webpackPrefetch: true */ '../../components/modals/FavouriteBikePositionModal.vue'),
    FinancingBlock,
  },
  transition: 'fade',
  props: {
    product: {
      type: [Object, null] as PropType<Product>,
      default: null,
    },
    isFetching: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const qty = ref(1);
    const product = toRef(props, 'product');

    const {
      addItem, error: cartError, loading: isCartLoading, canAddToCart,
    } = useCart();
    const { productGallery, imageSizes } = useProductGallery(product);
    const { activeTab, setActiveTab, openNewReviewTab } = useProductTabs();

    const { isAuthenticated } = useUser();
    const { addOrRemoveItem, isInWishlist } = useWishlist();

    const productShortDescription = computed(
      () => props.product?.short_description?.html || '',
    );

    const configurableOptions = computed(
      () => props.product?.configurable_options ?? [],
    );

    const leftImage = computed(() => (productGallery.value.length > 1 ? productGallery.value.pop()?.big.url : ''));

    const productConfiguration: Ref<{ size?: string }> = ref({});
    const productPrice = computed(() => getConfigurableProductPriceCommand(props.product));
    const productSpecialPrice = computed(() => getConfigurableProductSpecialPriceCommand(props.product));
    const addToCartError = computed(() => cartError.value?.addItem?.message);

    const customAttributes = computed(() => props.product.custom);

    // get EANs for variants (simple products)
    const productEans = computed(() => props.product.variants?.map((variant) => {
      if (variant.product?.custom?.manufacturer_ean) {
        return `${variant.product.custom.manufacturer_ean} (${variant.attributes?.[0]?.label})`;
      }
      return null;
    }));

    const currentProductVariation: Ref<ConfigurableProductOptionsValues> = ref({});

    const productInShop = computed(() => customAttributes.value?.exhibited_in_shop);

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const productStock = computed(() => props.product.variants?.reduce((accumulator, variant) => accumulator + variant.product.quantity, 0));
    const productOutOfStock = computed(() => productStock.value === 0);
    const hideBadgeDivider = computed(() => !productInShop.value && productOutOfStock.value);

    const displayOnlyOneStockLeft = computed(() => {
      const selectedVariant = currentProductVariation.value;
      if (!selectedVariant?.uid) {
        return productStock.value === 1;
      }

      // variant selected
      return props.product.variants?.some(
        (productVariant) => productVariant.attributes[0].label === selectedVariant.label && productVariant.product.quantity === 1,
      );
    });

    const noProductSelected = false;

    const descriptionHeadlineText = computed(() => ({
      headline: customAttributes.value?.kohl_description_headline,
      text: customAttributes.value?.kohl_description,
    }));

    const shortDescriptionHeadlineText = computed(() => ({
      headline: customAttributes.value?.kohl_short_description_headline,
      text: customAttributes.value?.kohl_short_description,
    }));

    const noteHeadlineText = computed(() => ({
      headline: customAttributes.value?.kohl_note_headline,
      text: customAttributes.value?.kohl_note,
    }));

    const { getMatchingBikes, getShowMatchingBikes } = useFrametool();

    const userFrameSize = ref<string | null>(null);
    const showMatchingBikes = ref<boolean>(false);

    const shippingUrl = '/hilfe-und-kontakt/versand-lieferung/';
    const isAddBikeToCartModalOpen = ref(false);
    const isFavouriteBikePositionModalOpen = ref(false);

    const manufacturerInfo: Ref<ManufacturerInfo | null> = ref(null);
    const { getManufacturerInfo } = useManufacturerApi();

    watch(() => customAttributes, async () => {
      const manufacturer = customAttributes.value?.manufacturer_name;
      if (manufacturer?.length) {
        manufacturerInfo.value = await getManufacturerInfo(manufacturer);
      }
    }, { immediate: true, deep: true });

    const toggleAddBikeToCartModal = () => {
      isAddBikeToCartModalOpen.value = !isAddBikeToCartModalOpen.value;
    };

    const toggleFavouriteBikePositionModal = () => {
      isFavouriteBikePositionModalOpen.value = !isFavouriteBikePositionModalOpen.value;

      if (isAddBikeToCartModalOpen.value) toggleAddBikeToCartModal();
    };

    const fallbackAction: Ref<() => void | null> = ref(null);
    const currentProductConfiguration = ref({});
    const currentProductQuantity = ref(0);
    const businessBikeUrlQuery = `${props.product.name} - ${props.product.sku}`;
    const businessBikeUrl = `/dienstrad/dienstrad-erwerben?${bikeNameQueryParameter}=${encodeURIComponent(businessBikeUrlQuery)}`;

    return {
      addItem,
      addItemToWishlist: addOrRemoveItem,
      canAddToCart,
      configurableOptions,
      isAuthenticated,
      isInWishlist: computed(() => isInWishlist({ product: props.product })),
      isCartLoading,
      productConfiguration,
      productGallery,
      getProductName,
      getProductSwatchData,
      productPrice,
      productShortDescription,
      productSpecialPrice,
      qty,
      productInShop,
      productStock,
      imageSizes,
      setActiveTab,
      openNewReviewTab,
      activeTab,
      TabsConfig,
      addToCartError,
      currentProductVariation,
      getProductSku,
      descriptionHeadlineText,
      shortDescriptionHeadlineText,
      noteHeadlineText,
      customAttributes,
      arrowRightIcon,
      noProductSelected,
      leftImage,
      expertAdviceUrl,
      getExpertAdviceLink,
      shippingUrl,
      toggleAddBikeToCartModal,
      toggleFavouriteBikePositionModal,
      isAddBikeToCartModalOpen,
      isFavouriteBikePositionModalOpen,
      fallbackAction,
      currentProductConfiguration,
      currentProductQuantity,
      businessBikeUrl,
      getMatchingBikes,
      getShowMatchingBikes,
      userFrameSize,
      showMatchingBikes,
      displayOnlyOneStockLeft,
      productOutOfStock,
      hideBadgeDivider,
      manufacturerInfo,
      productEans,
    };
  },
  mounted() {
    // This is a hard fix, to force the images to load in every browser
    document.querySelectorAll('.sf-image').forEach((element: HTMLElement) => {
      // eslint-disable-next-line no-param-reassign
      element.style.display = 'inline-block';
    });
    if (this.productGallery.length === 1) {
      // Force hiding the thumbnails
      const thumbsGallery = document.querySelectorAll('.sf-gallery__thumbs')[0] as HTMLElement;
      thumbsGallery.style.display = 'none';

      const primaryImage = document.querySelectorAll('.sf-gallery')[0] as HTMLElement;
      primaryImage.classList.add('sf-gallery--without-thumbs');
    }

    // needs to happen on client side as potentially the frame size is coming from the local storage
    this.updateFrameSize();
  },
  methods: {
    updateProductConfiguration(variant: ConfigurableProductOptionsValues) {
      this.currentProductVariation = variant;
      this.productConfiguration.size = variant.uid;
    },
    resetProductConfiguration() {
      this.currentProductVariation = {};
      this.productConfiguration = {};
    },
    addItemToCart(quantity: number, productConfiguration: any) {
      if (!this.currentProductVariation.uid) {
        this.noProductSelected = true;
      } else {
        this.noProductSelected = false;

        this.toggleAddBikeToCartModal();

        this.currentProductQuantity = quantity;
        this.currentProductConfiguration = productConfiguration;

        this.fallbackAction = () => {
          this.addItem({ product: this.product, quantity, productConfiguration });
          this.closeAllModals();
        };
      }
    },
    closeAllModals() {
      if (this.isAddBikeToCartModalOpen) this.toggleAddBikeToCartModal();
      if (this.isFavouriteBikePositionModalOpen) this.toggleFavouriteBikePositionModal();
    },
    getVariantSku(variant: ConfigurableProductOptionsValues) {
      // Fetching the 0 position because we only have one attribute for now
      return this.product.variants?.find((productVariant: ConfigurableVariant) => productVariant.attributes[0].label === variant.label).product.sku;
    },
    addBikeToCartSpecial(newOptions: { height: string, bellyButtonHeight: string, weight: string, seatPosition: string }) {
      this.closeAllModals();
      this.addItem({
        product: this.product,
        quantity: this.currentProductQuantity,
        productConfiguration: this.currentProductConfiguration,
        seatPositions: newOptions,
        variantSku: this.getVariantSku(this.currentProductVariation),
      });
    },
    async updateFrameSize() {
      this.showMatchingBikes = await this.getShowMatchingBikes();
      if (this.showMatchingBikes) {
        const matchingBikes = await this.getMatchingBikes();
        if (matchingBikes) {
          const matchingBike = matchingBikes
            .filter((bike: MatchingBikeInfo) => bike.sku === this.product.sku)
            .map((bike: MatchingBikeInfo) => bike.sizeLabel);
          this.userFrameSize = matchingBike.length > 0 ? matchingBike[0] : null;

          // calculate matching variant and preselect if there is a match
          // if no bike is found, we also want to reset the configuration in case another variant was selected before
          if (this.userFrameSize) {
            const matchingVariant = this.configurableOptions[0]?.values.find((option) => option.label === this.userFrameSize);
            if (matchingVariant) {
              this.updateProductConfiguration(matchingVariant);
            } else {
              this.resetProductConfiguration();
            }
          } else {
            this.resetProductConfiguration();
          }
        }
      }
    },
  },
});
